import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Newspaper, LifeBuoy, Ticket, User, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  requiresAuth: boolean;
  /** match nested routes (e.g. /catalogo/...) */
  matchPrefix?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Home", icon: Home, requiresAuth: false },
  { path: "/volantino", label: "Volantino", icon: Newspaper, requiresAuth: false },
  { path: "/concierge", label: "Aiuto", icon: LifeBuoy, requiresAuth: false },
  { path: "/ricompense", label: "Coupon", icon: Ticket, requiresAuth: true, matchPrefix: true },
  { path: "/profilo", label: "Account", icon: User, requiresAuth: true },
];

const ACTIVE = "#86694B";
const INACTIVE = "#9A958E";

const BottomNavigation = React.forwardRef<HTMLElement, Record<string, never>>(
  (_, ref) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleNavClick = (e: React.MouseEvent, item: NavItem) => {
      if (item.requiresAuth && !user) {
        e.preventDefault();
        navigate("/login", { state: { from: { pathname: item.path } } });
      }
    };

    const isItemActive = (item: NavItem) =>
      item.matchPrefix
        ? location.pathname === item.path || location.pathname.startsWith(item.path + "/")
        : location.pathname === item.path;

    return (
      <nav
        ref={ref}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#111]/[0.06]"
        style={{
          paddingBottom: "var(--safe-area-bottom, 0px)",
          boxShadow: "0 -4px 20px -12px rgba(17,17,17,0.18)",
        }}
      >
        <div className="flex items-stretch justify-around h-[58px] max-w-md mx-auto">
          {NAV_ITEMS.map((item) => {
            const active = isItemActive(item);
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(e, item)}
                className="relative flex-1 flex flex-col items-center justify-center gap-1 active:opacity-70 transition-opacity"
                aria-label={item.label}
              >
                {active && (
                  <span className="absolute top-0 h-[3px] w-7 rounded-full" style={{ background: ACTIVE }} />
                )}
                <Icon
                  className="w-[22px] h-[22px]"
                  style={{ color: active ? ACTIVE : INACTIVE }}
                  strokeWidth={active ? 2.4 : 1.8}
                />
                <span
                  className={cn("text-[10.5px] leading-none", active ? "font-semibold" : "font-medium")}
                  style={{ color: active ? ACTIVE : INACTIVE }}
                >
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export { BottomNavigation };
